import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

/**
 * Custom hook to track user session and screen time
 * @param {string} userEmail - Current user's email
 * @param {string} currentTicket - Currently viewing ticket ID (optional)
 * @returns {Object} Session statistics
 */
export const useSessionTracking = (userEmail, currentTicket = null) => {
  const sessionIdRef = useRef(null);
  const userEmailRef = useRef(null); // Store email in ref to persist during logout
  const ticketStartTimeRef = useRef(null);
  const intervalRef = useRef(null);
  const activeSegmentStartRef = useRef(null);
  const visibilityStateRef = useRef(!document.hidden);
  const focusStateRef = useRef(typeof document.hasFocus === 'function' ? document.hasFocus() : true);
  const [sessionStats, setSessionStats] = useState({
    totalTime: 0,
    activeTime: 0,
    ticketsViewed: 0
  });

  const canTrackActiveTime = () => visibilityStateRef.current && focusStateRef.current;

  const startActiveSegment = () => {
    if (!activeSegmentStartRef.current && canTrackActiveTime()) {
      activeSegmentStartRef.current = Date.now();
    }
  };

  // Update email ref when it changes
  useEffect(() => {
    if (userEmail) {
      userEmailRef.current = userEmail;
    }
  }, [userEmail]);

  // Initialize session on mount
  useEffect(() => {
    if (!userEmail) {
      console.log('[Session Tracking] No userEmail provided, skipping session tracking');
      return;
    }

    const startSession = async () => {
      try {
        // Generate unique session ID
        sessionIdRef.current = uuidv4();
        
        console.log('[Session Tracking] Starting session for:', userEmail);
        console.log('[Session Tracking] Session ID:', sessionIdRef.current);

        const response = await fetch('/api/session/start', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            userEmail,
            userName: userEmail.split('@')[0], // Extract username from email
            sessionId: sessionIdRef.current,
            userAgent: navigator.userAgent,
            ipAddress: '' // Backend can capture from request
          })
        });

        const result = await response.json();
        console.log('[Session Tracking] Session started:', result);
        
        if (!result.success) {
          console.error('[Session Tracking] Failed to start session:', result);
          return;
        }

        startActiveSegment();

        // Start heartbeat - flush active on-screen time every 30 seconds
        intervalRef.current = setInterval(() => {
          flushActiveTime('interval');
        }, 30000); // 30 seconds
        
        console.log('[Session Tracking] Heartbeat started (30s interval)');

      } catch (error) {
        console.error('[Session Tracking] Error starting session:', error);
      }
    };

    startSession();

    const handleVisibilityChange = async () => {
      visibilityStateRef.current = !document.hidden;

      if (!visibilityStateRef.current) {
        await flushActiveTime('visibility-hidden');
        activeSegmentStartRef.current = null;
      } else {
        startActiveSegment();
      }
    };

    const handleFocus = () => {
      focusStateRef.current = true;
      startActiveSegment();
    };

    const handleBlur = async () => {
      focusStateRef.current = false;
      await flushActiveTime('window-blur');
      activeSegmentStartRef.current = null;
    };

    // End session on unmount or page unload
    const handleUnload = () => {
      console.log('[Session Tracking] Ending session');
      flushActiveTime('unload');
      endSession();
    };

    window.addEventListener('beforeunload', handleUnload);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);
    window.addEventListener('blur', handleBlur);

    return () => {
      handleUnload();
      window.removeEventListener('beforeunload', handleUnload);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
      window.removeEventListener('blur', handleBlur);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [userEmail]);

  // Track ticket viewing time
  useEffect(() => {
    if (currentTicket) {
      // User opened a ticket - start timer
      ticketStartTimeRef.current = Date.now();
    } else {
      ticketStartTimeRef.current = null;
    }
  }, [currentTicket]);

  const sendHeartbeat = async ({ activeSecondsDelta = 0, reason = 'heartbeat' } = {}) => {
    const emailToUse = userEmailRef.current || userEmail;

    if (!sessionIdRef.current || !emailToUse) {
      console.log('[Session Tracking] Skipping heartbeat - no session or user');
      return;
    }

    try {
      // Track ticket time in deltas to avoid double-counting.
      let ticketTime = 0;
      if (currentTicket && ticketStartTimeRef.current) {
        ticketTime = activeSecondsDelta > 0
          ? activeSecondsDelta
          : Math.floor((Date.now() - ticketStartTimeRef.current) / 1000);

        ticketStartTimeRef.current = Date.now();
      }
      
      console.log('[Session Tracking] Sending heartbeat:', reason, 'Active:', activeSecondsDelta, 'Ticket:', currentTicket, 'Time:', ticketTime);

      const response = await fetch('/api/session/activity', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: sessionIdRef.current,
          userEmail: emailToUse,
          activeSecondsDelta,
          currentTicket,
          ticketTime
        })
      });

      const result = await response.json();
      console.log('[Session Tracking] Heartbeat response:', result);
      
      if (result.success && result.totalDuration) {
        setSessionStats(prev => ({
          ...prev,
          totalTime: result.totalDuration,
          activeTime: result.activeDuration ?? prev.activeTime
        }));
      }
    } catch (error) {
      console.error('[Session Tracking] Error sending heartbeat:', error);
    }
  };

  const flushActiveTime = async (reason = 'heartbeat') => {
    if (!activeSegmentStartRef.current) {
      return;
    }

    const now = Date.now();
    const activeSecondsDelta = Math.floor((now - activeSegmentStartRef.current) / 1000);
    activeSegmentStartRef.current = now;

    if (activeSecondsDelta <= 0) {
      return;
    }

    await sendHeartbeat({ activeSecondsDelta, reason });
  };

  const endSession = async () => {
    // Use ref to get email even if prop is null (during logout)
    const emailToUse = userEmailRef.current;
    
    if (!sessionIdRef.current || !emailToUse) {
      console.log('[Session Tracking] Cannot end session - no session ID or user email');
      return;
    }

    try {
      await flushActiveTime('end-session');

      console.log('[Session Tracking] Ending session:', sessionIdRef.current, 'for user:', emailToUse);
      
      const data = {
        sessionId: sessionIdRef.current,
        userEmail: emailToUse
      };

      // Use fetch with keepalive instead of sendBeacon for better reliability
      // sendBeacon doesn't set Content-Type header properly
      const response = await fetch('/api/session/end', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
        keepalive: true // Ensures request completes even if page is closing
      });

      const result = await response.json();
      console.log('[Session Tracking] Session ended successfully:', result);
      
    } catch (error) {
      console.error('[Session Tracking] Error ending session:', error);
    }
  };

  return {
    sessionId: sessionIdRef.current,
    sessionStats,
    sendHeartbeat, // Expose for manual trigger if needed
    endSession // Expose for manual session end
  };
};

export default useSessionTracking;
